import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, Clock, MessageCircle, Tag } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import useJsonLd from "@/hooks/useJsonLd";
import Markdown, { headingId } from "@/components/blog/Markdown";
import PostCard, { fmtDate } from "@/components/blog/PostCard";
import { articleLd, breadcrumb } from "@/lib/jsonLd";
import { setLandingKeyword } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Sidebar({ toc }) {
  return (
    <aside className="lg:sticky lg:top-24 space-y-6" data-testid="blog-sidebar">
      {toc.length > 0 && (
        <nav className="border border-slate-200 bg-white p-5" data-testid="blog-toc">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-3">In this article</div>
          <ol className="space-y-2">
            {toc.map((h, i) => (
              <li key={h}>
                <a href={`#${headingId(h)}`} className="flex gap-2 text-sm text-slate-700 hover:text-topo transition-colors">
                  <span className="mono text-[10px] text-orange-survey pt-0.5">{String(i + 1).padStart(2, "0")}</span> {h}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      )}
      <div className="bg-cyanotype text-white p-6" data-testid="blog-cta-card">
        <div className="mono text-[10px] tracking-[0.22em] uppercase text-orange-survey-light">Need this drafted?</div>
        <p className="display mt-2 text-xl font-extrabold tracking-tight">Send one job. Get a fixed quote in 24 hours.</p>
        <Link to="/contact#contact-brief" data-testid="blog-cta-quote" className="mt-5 inline-flex items-center gap-1.5 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-4 py-2 transition-colors">
          Request a quote <ArrowUpRight className="h-4 w-4" />
        </Link>
        <a href="https://wa.me/918083691588" target="_blank" rel="noopener noreferrer" data-testid="blog-cta-whatsapp" className="mt-3 flex items-center gap-2 text-sm text-slate-300 hover:text-white">
          <MessageCircle className="h-4 w-4" /> WhatsApp +91 80836 91588
        </a>
      </div>
    </aside>
  );
}

function ArticleHeader({ post }) {
  return (
    <header className="border-b border-slate-200 bg-paper-dark/30">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12 lg:py-16">
        <Link to="/blog" data-testid="blog-back-link" className="mono text-[10px] tracking-[0.22em] uppercase text-topo inline-flex items-center gap-1.5 hover:text-orange-survey">
          <ArrowLeft className="h-3 w-3" /> All guides
        </Link>
        <div className="mt-6 mono text-[10px] tracking-[0.22em] uppercase text-orange-survey" data-testid="blog-post-category">{post.category}</div>
        <h1 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900 max-w-4xl leading-[1.05]" data-testid="blog-post-title">
          {post.title}
        </h1>
        <p className="mt-5 max-w-3xl text-base sm:text-lg text-slate-600 leading-relaxed">{post.excerpt}</p>
        <div className="mt-6 flex flex-wrap items-center gap-3 mono text-[10px] tracking-[0.18em] uppercase text-slate-500">
          <span>{post.author}</span>
          <span className="h-1 w-1 rounded-full bg-slate-300" />
          <span>{fmtDate(post.published_at)}</span>
          <span className="h-1 w-1 rounded-full bg-slate-300" />
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {post.read_minutes} min read</span>
        </div>
      </div>
    </header>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    axios
      .get(`${API}/blog/posts/${slug}`)
      .then((r) => { setData(r.data); setState("ready"); })
      .catch(() => setState("missing"));
  }, [slug]);

  const post = data?.post;
  useSeo({
    title: post ? `${post.title} — Tri-Axis XYZ` : "CAD Drafting Guide — Tri-Axis XYZ",
    description: post?.meta_description || post?.excerpt,
    type: "article",
  });
  useJsonLd(post ? articleLd(post) : null, "blog-article");
  useJsonLd(post ? breadcrumb([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }]) : null, "blog-article-bc");
  useEffect(() => { if (post) setLandingKeyword(pathname, post.target_keyword); }, [post, pathname]);

  const toc = useMemo(() => (post?.content || "").split("\n").filter((l) => l.startsWith("## ")).map((l) => l.slice(3).trim()), [post]);

  if (state === "loading") return <div className="mx-auto max-w-4xl px-6 py-24" data-testid="blog-post-loading"><div className="h-10 w-2/3 bg-paper-dark/50 animate-pulse" /><div className="mt-6 h-64 bg-paper-dark/30 animate-pulse" /></div>;
  if (state === "missing") {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center" data-testid="blog-post-missing">
        <h1 className="display text-3xl font-extrabold tracking-tight text-slate-900">This guide isn't available.</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-1.5 text-topo font-semibold"><ArrowLeft className="h-4 w-4" /> Browse all guides</Link>
      </div>
    );
  }

  return (
    <article data-testid="page-blog-post">
      <ArticleHeader post={post} />
      <div className="bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 max-w-3xl">
            <Markdown content={post.content} testid="blog-post-body" />
            {post.tags?.length > 0 && (
              <div className="mt-12 pt-6 border-t border-slate-200 flex flex-wrap items-center gap-2" data-testid="blog-post-tags">
                <Tag className="h-3.5 w-3.5 text-slate-400" />
                {post.tags.map((t) => <span key={t} className="mono text-[10px] tracking-wide uppercase border border-slate-300 px-2 py-1 text-slate-600">{t}</span>)}
              </div>
            )}
          </div>
          <div className="lg:col-span-4"><Sidebar toc={toc} /></div>
        </div>
      </div>
      {data.related.length > 0 && (
        <section className="border-t border-slate-200 bg-paper-dark/30 py-14" data-testid="blog-related">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Keep reading</div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">{data.related.map((p) => <PostCard key={p.slug} post={p} />)}</div>
          </div>
        </section>
      )}
    </article>
  );
}
